import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Body,
  Heading,
} from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { EmptyState } from '@cognite/cogs-core-v11/components/empty-state/empty-state.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';
import { Skeleton } from '@cognite/cogs-core-v11/components/skeleton/skeleton.tsx';
import { ThreeDIllustration } from '@cognite/cogs-icons-v11/illustrations/ThreeDIllustration.tsx';

import type { ThreeDModelIdentifier } from '../types';

import * as S from './elements';
import { getModelDetailsContext } from './model-details.context';

export type Props = {
  onClick: () => void;
  onlyImage?: boolean;
} & ThreeDModelIdentifier;

export const ModelDetails: FC<Props> = ({
  modelId,
  revisionId,
  onClick,
  onlyImage = false,
}): JSX.Element => {
  const { t } = useTranslation('asset');

  const { useRevision, useFetchImageByUrl } = useContext(
    getModelDetailsContext()
  );

  const { data: modelWithRevision, isLoading } = useRevision(
    modelId,
    revisionId
  );

  const { data: thumbnailDataURL, isLoading: isLoadingThumbnail } =
    useFetchImageByUrl(modelWithRevision?.thumbnailURL);

  if (isLoading) {
    return (
      <Skeleton.Rectangle
        width="388px"
        height="250px"
        data-testid="loading-state"
      />
    );
  }

  return (
    <Flex onClick={onClick} direction="column">
      <S.ImageWrapper>
        {isLoadingThumbnail && (
          <Skeleton.Rectangle
            width="388px"
            height="220px"
            data-testid="thumbnail-loading-state"
          />
        )}
        {!thumbnailDataURL && !isLoadingThumbnail && (
          <EmptyState
            illustration={<ThreeDIllustration />}
            title={t(
              'ASSET_OVERVIEW_THREED_CARD_NO_THUMBNAIL_TITLE',
              'No thumbnail to display'
            )}
            description={t(
              'ASSET_OVERVIEW_THREED_CARD_NO_THUMBNAIL_BODY',
              "The 3D model can still be accessed by pressing the 'Open' button."
            )}
          />
        )}
        {thumbnailDataURL && !isLoadingThumbnail && (
          <S.ImgStyled alt={modelWithRevision?.name} src={thumbnailDataURL} />
        )}
      </S.ImageWrapper>

      {!onlyImage && (
        <Flex direction="column">
          <Heading level={3}>{modelWithRevision?.name}</Heading>

          <Flex onClick={onClick} gap={8}>
            <Flex direction="column" gap={8}>
              <Body size="x-small" strong>
                modelId
              </Body>
              <Input fullWidth value={modelId} disabled />
              <Body size="x-small">3D viewer modelId</Body>
            </Flex>

            <Flex direction="column" gap={8}>
              <Body size="x-small" strong>
                revisionId
              </Body>
              <Input fullWidth value={revisionId} disabled />
              <Body size="x-small">3D viewer revisionId</Body>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
